import type { RequestConfig, RequestInterceptor, RequestMeta, RequestOptions } from 'uview-pro'
import { useUserStore } from '@/stores'

/**
 * 后端 API 网关前缀：所有接口统一走 /api
 * 环境变量只注入域名（host），前缀在此集中拼接，避免散落拼错
 */
const API_PREFIX = '/api'

const API_HOST = (import.meta.env.VITE_API_BASE_URL as string || '')
  .trim()
  .replace(/\/+$/, '') // 去尾部斜杠
  .replace(/\/api$/, '') // 兼容旧配置里已带 /api 的情况，防止拼成 /api/api

/** 最终 baseUrl：如 https://jf-sit.longjintech.com/api 或 /api（同源+代理） */
const baseUrl = `${API_HOST}${API_PREFIX}`

/** 统一响应结构 */
export interface ApiResponse<T = any> {
  code: number
  message: string
  data: T
}

/** 未登录/失效业务码 */
const UNAUTHORIZED_CODES = [401, 4001]

/** 防止并发 401 重复跳转 */
let redirectingToLogin = false

function isLoginPage(): boolean {
  const pages = getCurrentPages()
  const current = pages[pages.length - 1] as any
  return Boolean(current && current.route && current.route.includes('login'))
}

function showToast(title: string) {
  if (!title)
    return
  uni.showToast({ title, icon: 'none', duration: 2000 })
}

/**
 * 统一处理登录失效：
 * 1. 清空用户态（内存 + 由持久化插件同步 storage）
 * 2. toast 提示（登录页自身只提示不跳转）
 * 3. 防抖后 reLaunch 到登录页
 */
function handleUnauthorized(message: string, meta: RequestMeta = {}) {
  const userStore = useUserStore()
  userStore.logout()

  if (isLoginPage()) {
    meta.toast !== false && showToast(message || '登录已失效，请重新登录')
    return
  }
  if (redirectingToLogin)
    return
  redirectingToLogin = true

  meta.toast !== false && showToast(message || '登录已失效，请重新登录')
  setTimeout(() => {
    uni.reLaunch({
      url: '/pages/login/login',
      complete: () => {
        setTimeout(() => {
          redirectingToLogin = false
        }, 1000)
      },
    })
  }, 600)
}

// 全局配置
const httpRequestConfig: RequestConfig = {
  baseUrl,
  header: {
    'content-type': 'application/json',
  },
  timeout: 30000,
  meta: {
    originalData: true,
    toast: true,
    loading: true,
  },
}

// 请求/响应拦截器
const httpInterceptor: RequestInterceptor = {
  // 请求拦截器：注入 token
  request: (config: RequestOptions) => {
    const meta: RequestMeta = config.meta || {}
    if (meta.loading !== false) {
      uni.showLoading({ title: '加载中...', mask: true })
    }
    const userStore = useUserStore()
    if (userStore.token) {
      config.header.Authorization = `Bearer ${userStore.token}`
    }
    return config
  },
  // 响应拦截器：统一解包 { code, message, data }
  response: async (response: any) => {
    const meta: RequestMeta = response.config?.meta || {}
    uni.hideLoading()

    const { statusCode, data: rawData, errMsg } = response as any

    if (errMsg && (errMsg.includes('request:fail') || errMsg.includes('Failed to connect'))) {
      meta.toast !== false && showToast('网络异常，请稍后重试')
      throw new Error('网络异常，请稍后重试')
    }

    // HTTP 层 401（如网关直接拦截）
    if (statusCode === 401) {
      handleUnauthorized('登录已过期，请重新登录', meta)
      throw new Error('登录已过期，请重新登录')
    }

    if (!(statusCode >= 200 && statusCode < 300)) {
      const errorMessage = `请求错误[${statusCode}]`
      meta.toast !== false && showToast(errorMessage)
      throw new Error(errorMessage)
    }

    // 后端统一响应 { code, message, data }
    const res = rawData as ApiResponse
    if (res === null || typeof res !== 'object' || res.code === undefined) {
      throw new Error('响应格式异常！')
    }

    // 业务层未登录 / token 过期 / token 无效
    if (UNAUTHORIZED_CODES.includes(res.code)) {
      handleUnauthorized(res.message || '登录已失效，请重新登录', meta)
      throw new Error(res.message || '未登录')
    }

    if (res.code !== 0) {
      meta.toast !== false && showToast(res.message || '操作失败')
      // 携带业务码抛出，调用方可按 code 精确分支（如签到关闭 3001）
      const err: any = new Error(res.message || `业务错误[${res.code}]`)
      err.code = res.code
      throw err
    }

    return res.data
  },
}

export { httpInterceptor, httpRequestConfig }
